'use client';

import React from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  Calculator,
  Clapperboard,
  FileText,
  FolderKanban,
  Gamepad2,
  Globe,
  HelpCircle,
  Handshake,
  Home,
  Layers,
  ListChecks,
  LucideIcon,
  ChevronDown,
  Megaphone,
  Menu,
  Package,
  Palette,
  Newspaper,
  Search,
  Shield,
  ShoppingCart,
  Smartphone,
  Users,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { trackCTAClick } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import Button from "@/components/ui/Button";
import { Container } from "@/components/ui/Section";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

type HeaderProps = {
  variant?: "default" | "simple";
};

type LinkItem = {
  title: string;
  href: string;
  icon: LucideIcon;
  description?: string;
};

type LocalizedCopy = {
  title: string;
  description?: string;
};

type LocalizedLinkDefinition = {
  path: string;
  icon: LucideIcon;
  copy: {
    en: LocalizedCopy;
    el: LocalizedCopy;
  };
};

const SERVICE_ITEMS: LocalizedLinkDefinition[] = [
  {
    path: "/services",
    icon: Layers,
    copy: {
      en: {
        title: "Services Overview",
        description: "Explore every capability in a single view",
      },
      el: {
        title: "Επισκόπηση Υπηρεσιών",
        description: "Δείτε όλες τις υπηρεσίες μας σε μία ματιά",
      },
    },
  },
  {
    path: "/services/web-design",
    icon: Globe,
    copy: {
      en: {
        title: "Web Design & Development",
        description: "Custom responsive websites tailored to your brand",
      },
      el: {
        title: "Κατασκευή Ιστοσελίδων",
        description: "Εξατομικευμένες responsive ιστοσελίδες για το brand σας",
      },
    },
  },
  {
    path: "/services/ecommerce",
    icon: ShoppingCart,
    copy: {
      en: {
        title: "Ecommerce Development",
        description: "Launch high-converting storefronts that scale with demand",
      },
      el: {
        title: "Κατασκευή E‑shop",
        description:
          "Λανσάρετε e‑shop υψηλής απόδοσης που κλιμακώνεται με τη ζήτηση",
      },
    },
  },
  {
    path: "/services/apps",
    icon: Smartphone,
    copy: {
      en: {
        title: "Mobile App Development",
        description: "Ship performant native and cross‑platform experiences",
      },
      el: {
        title: "Web & Mobiles Apps",
        description:
          "Native και cross-platform εμπειρίες υψηλής απόδοσης",
      },
    },
  },
  {
    path: "/services/marketing-strategy",
    icon: Megaphone,
    copy: {
      en: {
        title: "Marketing Strategy",
        description: "Growth playbooks backed by experimentation and data",
      },
      el: {
        title: "Στρατηγική Μάρκετινγκ",
        description: "Σχέδια ανάπτυξης βασισμένα σε δεδομένα & πειραματισμό",
      },
    },
  },
  {
    path: "/services/seo",
    icon: Search,
    copy: {
      en: {
        title: "SEO Optimization",
        description: "Technical and content optimization for organic growth",
      },
      el: {
        title: "SEO Βελτιστοποίηση",
        description: "Τεχνική και περιεχομενική βελτιστοποίηση για οργανική ανάπτυξη",
      },
    },
  },
  {
    path: "/services/branding",
    icon: Palette,
    copy: {
      en: {
        title: "Branding Systems",
        description: "Build a memorable identity across every touchpoint",
      },
      el: {
        title: "Ολοκληρωμένο Branding",
        description: "Χτίζουμε αναγνωρίσιμη ταυτότητα σε κάθε σημείο επαφής",
      },
    },
  },
  {
    path: "/services/content-production",
    icon: Clapperboard,
    copy: {
      en: {
        title: "Content Production",
        description: "Video, copy, and creative assets crafted in-house",
      },
      el: {
        title: "Παραγωγή Περιεχομένου",
        description: "Video, copy και δημιουργικά assets in-house",
      },
    },
  },
  {
    path: "/consultation",
    icon: Handshake,
    copy: {
      en: {
        title: "Consultation",
        description: "Schedule a tailored discovery call with our team",
      },
      el: {
        title: "Δωρεάν Συμβουλευτική",
        description:
          "Κλείστε μια εξατομικευμένη κλήση γνωριμίας με την ομάδα",
      },
    },
  },
  {
    path: "/package-selection",
    icon: Package,
    copy: {
      en: {
        title: "Package Selection",
        description: "Compare deliverables across curated service bundles",
      },
      el: {
        title: "Σύγκριση Πακέτων",
        description: "Συγκρίνετε παραδοτέα ανάμεσα στα πακέτα υπηρεσιών",
      },
    },
  },
];

const COMPANY_ITEMS: LocalizedLinkDefinition[] = [
  {
    path: "/consultation",
    icon: Handshake,
    copy: {
      en: {
        title: "Consultation",
        description: "Schedule a tailored discovery call with our team",
      },
      el: {
        title: "Δωρεάν Συμβουλευτική",
        description:
          "Κλείστε μια εξατομικευμένη κλήση γνωριμίας με την ομάδα",
      },
    },
  },
  {
    path: "/package-selection",
    icon: Package,
    copy: {
      en: {
        title: "Package Selection",
        description: "Compare deliverables across curated service bundles",
      },
      el: {
        title: "Σύγκριση Πακέτων",
        description: "Συγκρίνετε παραδοτέα ανάμεσα στα πακέτα υπηρεσιών",
      },
    },
  },
  {
    path: "/projects",
    icon: FolderKanban,
    copy: {
      en: {
        title: "Projects",
        description: "Filter client work by industry and platform",
      },
      el: {
        title: "Έργα",
        description: "Φιλτράρετε έργα ανά κλάδο και πλατφόρμα",
      },
    },
  },
  {
    path: "/about",
    icon: Users,
    copy: {
      en: {
        title: "About",
        description: "Meet the product, design, and engineering team",
      },
      el: {
        title: "Σχετικά",
        description:
          "Γνωρίστε την ομάδα προϊόντος, design και ανάπτυξης",
      },
    },
  },
  {
    path: "/privacy-policy",
    icon: Shield,
    copy: {
      en: {
        title: "Privacy Policy",
        description: "Understand how we protect and process your data",
      },
      el: {
        title: "Πολιτική Απορρήτου",
        description:
          "Μάθετε πώς προστατεύουμε και επεξεργαζόμαστε τα δεδομένα σας",
      },
    },
  },
  {
    path: "/cookie-policy",
    icon: FileText,
    copy: {
      en: {
        title: "Cookie Policy",
        description: "See how we use cookies to personalise experiences",
      },
      el: {
        title: "Πολιτική Cookies",
        description:
          "Δείτε πώς χρησιμοποιούμε cookies για προσωποποίηση",
      },
    },
  },
  {
    path: "/tetris",
    icon: Gamepad2,
    copy: {
      en: {
        title: "Tetris Challenge",
        description: "Take a break with our playable Easter egg",
      },
      el: {
        title: "Πρόκληση Tetris",
        description: "Κάντε ένα διάλειμμα με το παιχνίδι-έκπληξη Tetris",
      },
    },
  },
];

const RESOURCES_ITEMS: LocalizedLinkDefinition[] = [
  {
    path: "/articles",
    icon: Newspaper,
    copy: {
      en: {
        title: "Articles",
        description:
          "Read the latest news, technical articles, and industry insights.",
      },
      el: {
        title: "Άρθρα",
        description:
          "Διαβάστε τα τελευταία νέα, τεχνικά άρθρα και ενημερώσεις για τον κλάδο.",
      },
    },
  },
  {
    path: "/marketing-cost",
    icon: Calculator,
    copy: {
      en: {
        title: "Marketing Cost Calculator",
        description: "Forecast spend and ROI before campaigns go live",
      },
      el: {
        title: "Υπολογιστής Κόστους Marketing",
        description:
          "Υπολογίστε budget και ROI πριν ξεκινήσουν οι καμπάνιες",
      },
    },
  },
  {
    path: "/checklist",
    icon: ListChecks,
    copy: {
      en: {
        title: "Free Website Checklist",
        description: "Download the free professional website checklist.",
      },
      el: {
        title: "Δωρεάν Checklist Ιστοσελίδας",
        description:
          "Κατεβάστε το δωρεάν checklist επαγγελματικής ιστοσελίδας.",
      },
    },
  },
  {
    path: "/resources/faq",
    icon: HelpCircle,
    copy: {
      en: {
        title: "FAQs",
        description:
          "Answers to common questions about processes and services.",
      },
      el: {
        title: "Συχνές Ερωτήσεις",
        description:
          "Απαντήσεις σε κοινές απορίες σχετικά με διαδικασίες και υπηρεσίες.",
      },
    },
  },
];

export function Header({ variant = "default" }: HeaderProps) {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [mobileSectionsOpen, setMobileSectionsOpen] = React.useState({
    services: true,
    resources: true,
    company: true,
  });
  const scrolled = useScroll(10);
  const isSimple = variant === "simple";
  const showBackground = isSimple || scrolled;

  const withPrefix = React.useCallback(
    (path: string) => {
      if (language === "en") {
        if (path === "/") {
          return "/en";
        }
        return `/en${path}`;
      }
      return path;
    },
    [language],
  );

  const servicesLinks = React.useMemo<LinkItem[]>(
    () =>
      SERVICE_ITEMS.filter((item) => item.path !== "/package-selection").map((item) => ({
        icon: item.icon,
        href: withPrefix(item.path),
        title: item.copy[language].title,
        description: item.copy[language].description,
      })),
    [language, withPrefix],
  );

  const servicesFeatured = React.useMemo(
    () => servicesLinks.slice(0, 6),
    [servicesLinks],
  );
  const servicesSupporting = React.useMemo(
    () => servicesLinks.slice(6),
    [servicesLinks],
  );

  const companyLinks = React.useMemo<LinkItem[]>(
    () =>
      COMPANY_ITEMS.filter((item) => item.path !== "/package-selection").map((item) => ({
        icon: item.icon,
        href: withPrefix(item.path),
        title: item.copy[language].title,
        description: item.copy[language].description,
      })),
    [language, withPrefix],
  );

  const companyFeatured = React.useMemo(
    () => companyLinks.slice(0, 3),
    [companyLinks],
  );
  const companySupporting = React.useMemo(
    () => companyLinks.slice(3),
    [companyLinks],
  );

  const resourcesLinks = React.useMemo<LinkItem[]>(
    () =>
      RESOURCES_ITEMS.map((item) => ({
        icon: item.icon,
        href: withPrefix(item.path),
        title: item.copy[language].title,
        description: item.copy[language].description,
      })),
    [language, withPrefix],
  );

  const resourcesFeatured = React.useMemo(
    () => resourcesLinks.slice(0, 2),
    [resourcesLinks],
  );
  const resourcesSupporting = React.useMemo(
    () => resourcesLinks.slice(2),
    [resourcesLinks],
  );

  React.useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!isMobileMenuOpen) {
      setMobileSectionsOpen({
        services: true,
        resources: true,
        company: true,
      });
    }
  }, [isMobileMenuOpen]);

  const handleConsultation = React.useCallback(() => {
    trackCTAClick(
      language === "el" ? "Κλείστε συμβουλευτική" : "Book a consultation",
      "header",
      "primary"
    );
    window.navigateToConsultation?.();
  }, [language]);

  const headerPadding = isSimple
    ? "pt-6 pb-4 md:pt-8 md:pb-6"
    : "pt-12 pb-8 md:pt-14 md:pb-10";

  const isActive = React.useCallback(
    (href: string) => location.pathname === href,
    [location.pathname]
  );

  const servicesLabel = language === "el" ? "Υπηρεσίες" : "Services";
  const resourcesLabel = language === "el" ? "Πηγές" : "Resources";
  const companyLabel = language === "el" ? "Εταιρεία" : "Company";
  const homeLabel = language === "el" ? "Αρχική" : "Home";
  const homeHref = withPrefix("/");
  const homeLink: LinkItem = {
    title: homeLabel,
    href: homeHref,
    icon: Home,
    description:
      language === "el"
        ? "Επιστροφή στην αρχική σελίδα"
        : "Back to the homepage",
  };
  const consultationLead =
    language === "el" ? "Έτοιμοι να ξεκινήσουμε;" : "Ready to start?";
  const consultationAction =
    language === "el" ? "Κλείστε συμβουλευτική" : "Book a consultation";
  const logoAriaLabel =
    language === "el"
      ? "Πλοήγηση στην αρχική ενότητα"
      : "Navigate to the home section";
  const consultationAriaLabel =
    language === "el"
      ? "Κλείστε δωρεάν συμβουλευτική"
      : "Book a free consultation";
  const openMenuAriaLabel =
    language === "el"
      ? "Άνοιγμα μενού πλοήγησης"
      : "Open navigation menu";
  const closeMenuAriaLabel =
    language === "el"
      ? "Κλείσιμο μενού πλοήγησης"
      : "Close navigation menu";
  const toggleMenuAriaLabel = isMobileMenuOpen
    ? closeMenuAriaLabel
    : openMenuAriaLabel;
  const toggleMobileSection = (section: "services" | "resources" | "company") => {
    setMobileSectionsOpen((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 z-50 w-full border-b border-transparent transition-colors",
        showBackground && "border-white/10 bg-black/85 backdrop-blur"
      )}
    >
      <Container className={cn("flex items-center justify-between", headerPadding)}>
        <Link
          to={withPrefix("/")}
          aria-label={logoAriaLabel}
          className="inline-flex"
          onClick={(event) => {
            setIsMobileMenuOpen(false);
            if (window.navigateToHome) {
              event.preventDefault();
              window.navigateToHome();
            }
          }}
        >
          <LogoMark />
        </Link>

        <nav
          className="hidden items-center gap-6 lg:flex"
          aria-label="Primary navigation"
        >
          <NavigationMenu className="max-w-none">
            <NavigationMenuList className="flex items-center gap-1">
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    to={homeHref}
                    className={cn(
                      "rounded-full px-4 py-2 text-base font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
                      isActive(homeHref) && "bg-white/10 text-white",
                    )}
                  >
                    {homeLabel}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="rounded-full px-4 py-2 text-base font-medium text-white transition data-[state=open]:bg-white/10 data-[state=open]:text-white hover:bg-white/10">
                  {servicesLabel}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="md:w-[560px] lg:w-[640px]">
                  <div className="rounded-[inherit] bg-black/70 backdrop-blur-xl p-4">
                    <div className="grid gap-4 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                      <ul className="grid gap-2 sm:grid-cols-2">
                        {servicesFeatured.map((item) => (
                          <li key={`services-featured-${item.title}`}>
                            <FeaturedNavItem {...item} active={isActive(item.href)} />
                          </li>
                        ))}
                      </ul>
                      {servicesSupporting.length ? (
                        <ul className="flex flex-col gap-1 rounded-xl border border-white/[0.05] bg-white/[0.04] p-3">
                          {servicesSupporting.map((item) => (
                            <li key={`services-support-${item.title}`}>
                              <SimpleNavItem {...item} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <div className="mt-4 rounded-xl border border-white/[0.05] bg-white/[0.04] px-4 py-3 text-sm text-white/70">
                      {consultationLead}{" "}
                      <Link
                        to={withPrefix("/consultation")}
                        className="font-medium text-white hover:underline"
                      >
                        {consultationAction}
                      </Link>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="rounded-full px-4 py-2 text-base font-medium text-white transition data-[state=open]:bg-white/10 data-[state=open]:text-white hover:bg-white/10">
                  {resourcesLabel}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="md:w-[560px] lg:w-[640px]">
                  <div className="rounded-[inherit] bg-black/70 backdrop-blur-xl p-4">
                    <div className="grid gap-4 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
                      <ul className="grid gap-2 sm:grid-cols-2">
                        {resourcesFeatured.map((item) => (
                          <li key={`resources-featured-${item.title}`}>
                            <FeaturedNavItem {...item} active={isActive(item.href)} />
                          </li>
                        ))}
                      </ul>
                      {resourcesSupporting.length ? (
                        <ul className="flex flex-col gap-1 rounded-xl border border-white/[0.05] bg-white/[0.04] p-3">
                          {resourcesSupporting.map((item) => (
                            <li key={`resources-support-${item.title}`}>
                              <SimpleNavItem {...item} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="rounded-full px-4 py-2 text-base font-medium text-white transition data-[state=open]:bg-white/10 data-[state=open]:text-white hover:bg-white/10">
                  {companyLabel}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="md:w-[520px] lg:w-[560px]">
                  <div className="rounded-[inherit] bg-black/70 backdrop-blur-xl p-4">
                    <div className="grid gap-4 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
                      <ul className="space-y-2">
                        {companyFeatured.map((item) => (
                          <li key={`company-featured-${item.title}`}>
                            <FeaturedNavItem {...item} active={isActive(item.href)} />
                          </li>
                        ))}
                      </ul>
                      {companySupporting.length ? (
                        <ul className="flex flex-col gap-1 rounded-xl border border-white/[0.05] bg-white/[0.04] p-3">
                          {companySupporting.map((item) => (
                            <li key={`company-support-${item.title}`}>
                              <SimpleNavItem {...item} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageToggle />
          <Button
            as="button"
            variant="primary"
            size="sm"
            onClick={handleConsultation}
            aria-label={consultationAriaLabel}
            className="px-5"
          >
            {t("nav.consultation")}
          </Button>
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full p-2 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 lg:hidden"
          aria-label={toggleMenuAriaLabel}
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen((open) => !open)}
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </Container>

      <MobileMenu
        open={isMobileMenuOpen}
        onDismiss={() => setIsMobileMenuOpen(false)}
        className="mx-auto flex h-full w-full max-w-5xl flex-col gap-6 overflow-y-auto px-4 py-6"
      >
        <div className="flex items-center justify-between">
          <LanguageToggle />
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label={closeMenuAriaLabel}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <MobileNavItem
              {...homeLink}
              onNavigate={() => setIsMobileMenuOpen(false)}
            />
          </div>

          <div>
            <button
              type="button"
              onClick={() => toggleMobileSection("services")}
              aria-expanded={mobileSectionsOpen.services}
              aria-controls="mobile-services-section"
              className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-white/60 transition hover:text-white"
            >
              <span>{servicesLabel}</span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  mobileSectionsOpen.services && "rotate-180"
                )}
              />
            </button>
            {mobileSectionsOpen.services ? (
              <div id="mobile-services-section" className="mt-2 space-y-2">
                {servicesLinks.map((link) => (
                  <MobileNavItem
                    key={`mobile-services-${link.title}`}
                    {...link}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <button
              type="button"
              onClick={() => toggleMobileSection("resources")}
              aria-expanded={mobileSectionsOpen.resources}
              aria-controls="mobile-resources-section"
              className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-white/60 transition hover:text-white"
            >
              <span>{resourcesLabel}</span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  mobileSectionsOpen.resources && "rotate-180"
                )}
              />
            </button>
            {mobileSectionsOpen.resources ? (
              <div id="mobile-resources-section" className="mt-2 space-y-2">
                {resourcesLinks.map((link) => (
                  <MobileNavItem
                    key={`mobile-resources-${link.title}`}
                    {...link}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <button
              type="button"
              onClick={() => toggleMobileSection("company")}
              aria-expanded={mobileSectionsOpen.company}
              aria-controls="mobile-company-section"
              className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-white/60 transition hover:text-white"
            >
              <span>{companyLabel}</span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  mobileSectionsOpen.company && "rotate-180"
                )}
              />
            </button>
            {mobileSectionsOpen.company ? (
              <div id="mobile-company-section" className="mt-2 space-y-2">
                {companyLinks.map((link) => (
                  <MobileNavItem
                    key={`mobile-company-${link.title}`}
                    {...link}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-auto flex flex-col gap-2 pb-2">
          <Button
            as="button"
            variant="primary"
            className="w-full justify-center"
            onClick={() => {
              setIsMobileMenuOpen(false);
              handleConsultation();
            }}
          >
            {t("nav.consultation")}
          </Button>
        </div>
      </MobileMenu>
    </header>
  );
}

type MobileMenuProps = React.ComponentProps<"div"> & {
  open: boolean;
  onDismiss?: () => void;
};

function MobileMenu({ open, onDismiss, className, children, ...props }: MobileMenuProps) {
  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onDismiss]);

  if (!open || typeof window === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-40 bg-black/85 backdrop-blur-md"
      onClick={() => onDismiss?.()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "absolute inset-x-0 top-0 flex h-full flex-col gap-6 border-t border-white/10 bg-black",
          className
        )}
        onClick={(event) => event.stopPropagation()}
        {...props}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

function FeaturedNavItem({
  title,
  description,
  icon: Icon,
  href,
  active,
}: LinkItem & { active?: boolean }) {
  return (
    <NavigationMenuLink asChild>
      <Link
        to={href}
        className={cn(
          "group flex h-full items-center gap-3 rounded-xl border border-white/[0.05] bg-white/[0.04] p-4 transition-colors hover:border-white/20 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
          active && "border-white/40 bg-white/15 text-white"
        )}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-black/60 text-white/70 group-hover:text-white">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="flex flex-col text-left">
          <span className="text-sm font-semibold text-white">{title}</span>
          {description ? (
            <span className="text-xs text-white/60 line-clamp-2">
              {description}
            </span>
          ) : null}
        </div>
      </Link>
    </NavigationMenuLink>
  );
}

function SimpleNavItem({ title, icon: Icon, href }: LinkItem) {
  return (
    <NavigationMenuLink asChild>
      <Link
        to={href}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/80 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
      >
        <Icon className="h-4 w-4 text-white/60" aria-hidden="true" />
        <span className="font-medium">{title}</span>
      </Link>
    </NavigationMenuLink>
  );
}

function MobileNavItem({
  title,
  description,
  icon: Icon,
  href,
  onNavigate,
}: LinkItem & { onNavigate: () => void }) {
  return (
    <Link
      to={href}
      onClick={onNavigate}
      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-left transition hover:border-white/20 hover:bg-white/10"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-black/60 text-white/70">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="flex flex-col">
        <span className="text-sm font-medium text-white">{title}</span>
        {description ? (
          <span className="text-xs text-white/60 line-clamp-2">
            {description}
          </span>
        ) : null}
      </div>
    </Link>
  );
}

function useScroll(threshold: number) {
  const [scrolled, setScrolled] = React.useState(false);

  const onScroll = React.useCallback(() => {
    setScrolled(window.scrollY > threshold);
  }, [threshold]);

  React.useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      onScroll();
    }
  }, [onScroll]);

  return scrolled;
}

const LOGO_PATTERN = [
  [1, 1, 1],
  [1, 0, 1],
  [1, 1, 1],
  [1, 0, 0],
];

function LogoMark() {
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = React.useState(false);
  const logoRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const renderPixels = () => {
    const logoPixels: JSX.Element[] = [];
    const logoSize =
      typeof window !== "undefined" && window.innerWidth < 768 ? 8 : 10;
    const logoGap = 1;
    const rect = logoRef.current?.getBoundingClientRect();

    let pixelId = 0;
    for (let row = 0; row < LOGO_PATTERN.length; row += 1) {
      for (let col = 0; col < LOGO_PATTERN[row].length; col += 1) {
        if (LOGO_PATTERN[row][col] !== 1) {
          continue;
        }

        const baseX = col * (logoSize + logoGap);
        const baseY = row * (logoSize + logoGap);
        const pixelCenterX = (rect?.left ?? 0) + baseX + logoSize / 2;
        const pixelCenterY = (rect?.top ?? 0) + baseY + logoSize / 2;
        const dx = mousePosition.x - pixelCenterX;
        const dy = mousePosition.y - pixelCenterY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = 100;

        let translateX = 0;
        let translateY = 0;
        let scale = 1;

        if (isHovering && distance < maxDistance && distance > 0) {
          const repulsion = (1 - distance / maxDistance) * 20;
          translateX = (-dx / distance) * repulsion;
          translateY = (-dy / distance) * repulsion;
          scale = 1 + (1 - distance / maxDistance) * 0.1;
        }

        const delay = (row + col) * 0.03;

        logoPixels.push(
          <span
            key={`logo-pixel-${pixelId}`}
            className="absolute bg-white"
            style={{
              width: `${logoSize}px`,
              height: `${logoSize}px`,
              left: `${baseX}px`,
              top: `${baseY}px`,
              transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
              transition: `transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${delay}s, box-shadow 0.2s ease ${delay}s`,
              boxShadow: isHovering
                ? "0 0 8px 1px rgba(255, 255, 255, 0.2)"
                : "none",
            }}
          />,
        );
        pixelId += 1;
      }
    }

    return logoPixels;
  };

  return (
    <div
      aria-hidden="true"
      ref={logoRef}
      className="relative h-[33px] w-[25px] cursor-pointer transition-transform duration-300 hover:scale-105 md:h-[39px] md:w-[29px]"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {renderPixels()}
    </div>
  );
}

export default Header;
